import { DataTable, DataTableRowEditCompleteEvent } from "primereact/datatable";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Column, ColumnEditorOptions, } from "primereact/column";
import { OtherUserModel } from "../../models/all-user/edit-user-model";
import { InputText } from "primereact/inputtext";
import React from "react";
import { Button } from "primereact/button";
import UserDeleteDialog from "./user-delete-dialog";
import editUserServices, { FlagHistoryItem } from "../../services/edit-users/edit-users.service";
import { Toast, ToastMessage } from "primereact/toast";
import { OrderModel } from "../../models/orders/order-list-model";
import Helpers from "../../utils/helpers";
import { Dialog } from "primereact/dialog";
import { InputTextarea } from "primereact/inputtextarea";
import { InputNumber } from "primereact/inputnumber";
import orderServices from "../../services/orders/orderservice";
import { Tag } from "primereact/tag";
import SimpleBar from 'simplebar-react';
import 'simplebar-react/dist/simplebar.min.css';
import './users-datatable.css';
import { Dropdown, DropdownFilterEvent } from "primereact/dropdown";
import { InputSwitch } from "primereact/inputswitch";

interface DataTableProps {
  data: OtherUserModel[];
}

const FLAG_LABELS: Record<number, string> = {
  0: 'No Flag',
  1: 'Red Flag',
  2: 'Blue Flag',
};

const employeeOptions = [
  { label: "Yes", value: true },
  { label: "No", value: false }
];

const UsersDataTable: React.FC<DataTableProps> = (props) => {
  const { t } = useTranslation();
  const toast = useRef<Toast>(null);
  const [userData, setUserData] = useState<OtherUserModel[] | any>([]);
  const [selectedId, setSelectedId] = useState<number>(0);
  const [notifyHeader, setNotifyHeader] = useState("");
  const [notifyMessage, setNotifyMessage] = useState("");
  const [isNotifyVisible, setIsNotifyVisible] = useState(false);

  const showMessage = (label: string, message: string, severity: ToastMessage['severity']) => {
    toast.current?.show({ severity, summary: label, detail: message, life: 3000 });
  };

  const [visible, setVisible] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<OrderModel | null>(null);
  const [instructionDialog, setInstructionDialog] = useState(false);
  const [instructionText, setInstructionText] = useState("");
  const [selectedCustomerUserId, setSelectedCustomerUserId] = useState<number | null>(null);
  const [pendingInstructionRow, setPendingInstructionRow] = useState<OtherUserModel | null>(null);

  // ──────────────────────────────────────────────────────────────────────────
  // REFERRED BY DIALOG state
  // ──────────────────────────────────────────────────────────────────────────
  const [referrerDialog, setReferrerDialog] = useState(false);
  const [pendingReferrerRow, setPendingReferrerRow] = useState<OtherUserModel | null>(null);
  const [pendingReferrerValue, setPendingReferrerValue] = useState<number | null>(null);
  const [referrerSaving, setReferrerSaving] = useState(false);

  const blankUserDetail: any[] = [{ userID: 0, name: '', primaryContactNbr: '' }];
  const [orderUsers, setOrderUsers] = useState<any[]>(blankUserDetail);
  const [orderUser, setOrderUser] = useState<any[]>([]);

  useEffect(() => {
    setOrderUser(orderUsers.map((user: any) => ({
      userID: user.userID,
      detail: `${user.name}, ${user.primaryContactNbr}`,
    })));
  }, [orderUsers]);

  const handleDropdownFilter = (e: DropdownFilterEvent) => {
    if (e.filter && e.filter.length > 2) {
      orderServices.getOrderUsers(e.filter).then((response: any) => {
        if (response && response.length > 0) {
          setOrderUsers(response)
        }
      }).catch(err => console.log(err));
    }
  }

  const searchedUsersRef = useRef<any[]>([]);

  const openReferrerDialog = (rowData: OtherUserModel) => {
    setPendingReferrerRow(rowData);
    setPendingReferrerValue(rowData.referredBy || null);

    // Seed initial option if exists so Dropdown holds it gracefully
    if (rowData.referredBy && rowData.referredByName) {
      if (!orderUser.find(u => u.userID === rowData.referredBy)) {
        setOrderUser([{ userID: rowData.referredBy, detail: `${rowData.referredByName}, (Current)` }, ...orderUser]);
      }
    }
    setReferrerDialog(true);
  };

  const submitReferrerChange = async () => {
    if (!pendingReferrerRow) return;

    setReferrerSaving(true);
    try {
      let putData = {
        ...pendingReferrerRow,
        referredBy: pendingReferrerValue
      };

      const response = await editUserServices.updateUser(pendingReferrerRow.userID, putData);

      if (response?.isSuccess) {
        let selectedReferrerName = '';
        if (pendingReferrerValue) {
          const referrerSelected = orderUser.find((u: any) => u.userID === pendingReferrerValue);
          if (referrerSelected && referrerSelected.detail) {
            selectedReferrerName = referrerSelected.detail.split(',')[0].trim();
          } else {
            selectedReferrerName = pendingReferrerRow.referredByName || '';
          }
        }

        setUserData((prev: OtherUserModel[]) =>
          prev.map((u: OtherUserModel) =>
            u.userID === pendingReferrerRow.userID
              ? { ...u, referredBy: pendingReferrerValue ?? 0, referredByName: selectedReferrerName }
              : u
          )
        );
        showMessage('Referrer Update', 'Referred By updated successfully', 'success');
        setReferrerDialog(false);
      } else {
        showMessage('Referrer Update', response?.message || 'Failed to update referrer', 'error');
      }
    } catch {
      showMessage('Referrer Update', 'Error updating referrer', 'error');
    } finally {
      setReferrerSaving(false);
    }
  };

  // ──────────────────────────────────────────────────────────────────────────
  // FLAG COMMENT state
  // ──────────────────────────────────────────────────────────────────────────
  const [flagCommentDialog, setFlagCommentDialog] = useState(false);
  const [pendingFlagRow, setPendingFlagRow] = useState<OtherUserModel | null>(null);
  const [pendingFlagValue, setPendingFlagValue] = useState<number>(0);
  const [flagComment, setFlagComment] = useState('');
  const [flagSaving, setFlagSaving] = useState(false);

  // ──────────────────────────────────────────────────────────────────────────
  // FLAG HISTORY state
  // ──────────────────────────────────────────────────────────────────────────
  const [flagHistoryDialog, setFlagHistoryDialog] = useState(false);
  const [flagHistory, setFlagHistory] = useState<FlagHistoryItem[]>([]);
  const [flagHistoryUser, setFlagHistoryUser] = useState('');
  const [flagHistoryLoading, setFlagHistoryLoading] = useState(false);

  // ──────────────────────────────────────────────────────────────────────────
  // FLAG HANDLERS
  // ──────────────────────────────────────────────────────────────────────────

  /** Intercept flag click — open comment dialog instead of saving immediately */
  const handleFlagChange = (rowData: OtherUserModel, newFlag: number) => {
    setPendingFlagRow(rowData);
    setPendingFlagValue(newFlag);
    setFlagComment('');
    setFlagCommentDialog(true);
  };

  /** Called when user submits the comment dialog */
  const submitFlagChange = async () => {
    if (!pendingFlagRow) return;

    setFlagSaving(true);
    try {
      const response = await editUserServices.saveFlagWithComment(
        pendingFlagRow.userID,
        pendingFlagValue,
        flagComment.trim() || null
      );

      if (response?.isSuccess) {
        // Update local state so UI reflects immediately
        setUserData((prev: OtherUserModel[]) =>
          prev.map((u: OtherUserModel) =>
            u.userID === pendingFlagRow.userID ? { ...u, flagCode: pendingFlagValue } : u
          )
        );
        showMessage('Flag', 'Customer flag updated successfully', 'success');
        setFlagCommentDialog(false);
      } else {
        showMessage('Flag', response?.message || 'Failed to update flag', 'error');
      }
    } finally {
      setFlagSaving(false);
    }
  };

  /** Open flag history dialog for a customer */
  const openFlagHistory = async (rowData: OtherUserModel) => {
    setFlagHistoryUser(rowData.name);
    setFlagHistory([]);
    setFlagHistoryDialog(true);
    setFlagHistoryLoading(true);
    try {
      const history = await editUserServices.getFlagHistory(rowData.userID);
      setFlagHistory(history);
    } catch {
      showMessage('Flag History', 'Failed to load flag history', 'error');
    } finally {
      setFlagHistoryLoading(false);
    }
  };
  const employeeBodyTemplate = (rowData: OtherUserModel) => {
    return (
      <div className="flex align-items-center justify-content-center gap-2">
        <InputSwitch
          checked={rowData.isEmployee === true}
          disabled
          style={{ opacity: 1 }}
        />
      </div>
    );
  };
  const directRewardBodyTemplate = (rowData: OtherUserModel) => {
    return (
      <div className="flex align-items-center justify-content-center gap-2">
        <InputSwitch
          checked={rowData.isDirectEarning === true}
          disabled
          style={{ opacity: 1 }}
        />
      </div>
    );
  };

  const directRewardEditor = (options: ColumnEditorOptions) => {
    return (
      <div className="flex align-items-center justify-content-center gap-2">
        <InputSwitch
          checked={options.value === true}
          onChange={(e) => options.editorCallback!(e.value)}
        />
      </div>
    );
  };

  // Employee editor for DataTable (example of another editor type, not related to flags)
  const employeeEditor = (options: ColumnEditorOptions) => {
    return (
      <div className="flex align-items-center justify-content-center gap-2">
        <InputSwitch
          checked={options.value === true}
          onChange={(e) => options.editorCallback!(e.value)}
        />
      </div>
    );
  };

  // ──────────────────────────────────────────────────────────────────────────
  // FLAG UI COMPONENTS
  // ──────────────────────────────────────────────────────────────────────────

  const VerifiedBadge = ({ color, active, onClick }: { color: string; active: boolean; onClick: () => void }) => (
    <span
      onClick={onClick}
      title={active ? 'Click to remove flag' : 'Click to set flag'}
      style={{ cursor: 'pointer', opacity: active ? 1 : 0.25, display: 'inline-flex', transition: 'opacity 0.15s' }}
    >
      <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="11" cy="11" r="11" fill={color} />
        <path d="M6.5 11.5L9.5 14.5L15.5 8" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
  );

  const flagBodyTemplate = (rowData: OtherUserModel) => {
    const flag = rowData.flagCode ?? 0;
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.75rem' }}>
        <VerifiedBadge
          color="#ef4444"
          active={flag === 1}
          onClick={() => handleFlagChange(rowData, flag === 1 ? 0 : 1)}
        />
        <VerifiedBadge
          color="#3b82f6"
          active={flag === 2}
          onClick={() => handleFlagChange(rowData, flag === 2 ? 0 : 2)}
        />
      </span>
    );
  };

  const flagHistoryTemplate = (rowData: OtherUserModel) => (
    <Button
      icon="pi pi-history"
      tooltip="Flag History"
      className="p-button-rounded"
      style={{ border: 'none', color: '#6b7280', background: 'none' }}
      onClick={() => openFlagHistory(rowData)}
    />
  );

  // ──────────────────────────────────────────────────────────────────────────
  // EXISTING HANDLERS (unchanged)
  // ──────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    setUserData(props.data);
  }, [props.data]);

  const onRowEditComplete = async (event: DataTableRowEditCompleteEvent) => {
    let _data = [...userData];
    let { newData, index } = event;
    const oldData = _data[index];

    const primaryNumber = newData.primaryContactNbr?.toString() || "";
    const secondaryNumber = newData.secondaryContactNbr?.toString() || "";

    if (primaryNumber && (primaryNumber.length !== 10 || !/^[6-9]/.test(primaryNumber))) {
      showMessage('Validation Error', 'Primary contact number must be exactly 10 digits and start with 6, 7, 8, or 9', 'error');
      return;
    }

    if (secondaryNumber && (secondaryNumber.length !== 10 || !/^[6-9]/.test(secondaryNumber))) {
      showMessage('Validation Error', 'Secondary contact number must be exactly 10 digits and start with 6, 7, 8, or 9', 'error');
      return;
    }

    //  // Check if primary contact number changed and if open orders exist
    //   if (primaryNumber !== oldData.primaryContactNbr?.toString()) {
    //     try {
    //       const openOrders = await orderServices.GetExistingOpenOrders({ userID: newData.userID });
    //       if (openOrders && openOrders.length > 0) {
    //         showMessage('Restricted', 'Primary contact number cannot be changed while open orders exist.', 'warn');
    //         return;
    //       }
    //     } catch (error) {
    //       console.error("Error checking open orders:", error);
    //     }
    //   }

    const name = newData.name?.toString() || "";
    if (!name.trim()) {
      showMessage('Validation Error', 'User Name is required', 'error');
      return;
    } else if (/^\s/.test(name)) {
      showMessage('Validation Error', 'Name should not start with a space', 'error');
      return;
    } else if (/\s{2,}/.test(name)) {
      showMessage('Validation Error', 'Name should not have multiple consecutive spaces', 'error');
      return;
    }

    const email = newData.email?.toString() || "";
    if (email && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(email)) {
      showMessage('Validation Error', 'Not a valid email', 'error');
      return;
    }

    if (JSON.stringify(oldData) !== JSON.stringify(newData)) {
      let putData = {
        ...newData
      };
      let response = await editUserServices.updateUser(newData.userID, putData);
      if (response.isSuccess) {
        showMessage('User Update', 'User detail updated successfully', 'success');
        _data[index] = newData as OtherUserModel;
        setUserData(_data);
      } else {
        showMessage('User Update', response.message + ': Error in update user', 'error');
      }
    }
  };

  const dateBodyTemplate = (rowData: any) => formatDate(rowData.createdDate);
  const OrderdateBodyTemplate = (rowData: any) => formatDate(rowData.orderDate);
  const InvoicedateBodyTemplate = (rowData: any) => formatDate(rowData.invoiceDate);

  const deleteUser = async () => {
    try {
      let response = await editUserServices.deleteUser(selectedId);
      if (response.isSuccess) {
        showMessage('Delete User', 'User deleted successfully', 'success');
        const updatedData = updateStatusOnDelete(selectedId);
        setUserData(updatedData);
      } else {
        showMessage('Delete User', response.message + ': Error in delete user', 'error');
      }
    } catch (error) {
      console.error(error);
    }
  };

  function updateStatusOnDelete(id: number): OtherUserModel[] {
    return userData.map((item: OtherUserModel) => {
      if (item.userID === id) return { ...item, isDeleted: true };
      return item;
    });
  }

  const deleteActionTemplate = (rowData: OtherUserModel) => (
    <Button
      icon="pi pi-trash"
      onClick={() => onRowDelete(rowData)}
      style={{ border: 'none', color: 'gray', background: 'none' }}
      className="p-button-rounded p-mr-2"
    />
  );

  const onRowDelete = (rowData: OtherUserModel) => {
    setSelectedId(rowData.userID);
    setNotifyHeader("Delete User");
    setNotifyMessage("Are you sure you want to delete this user?");
    setIsNotifyVisible(true);
  };

  const hideNotifyDialog = (value: boolean) => {
    if (value) deleteUser();
    setIsNotifyVisible(false);
  };

  const statusBodyTemplate = (rowData: OtherUserModel) => (
    <Tag value={rowData.isDeleted ? "Deleted" : "Active"} severity={getSeverity(rowData.isDeleted)} />
  );

  const getSeverity = (value: boolean) => {
    switch (value) {
      case true: return 'danger';
      case false: return 'info';
      default: return null;
    }
  };

  const textEditor = (options: ColumnEditorOptions) => (
    <InputText type="text" value={options.value} className="expanding-editor"
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => options.editorCallback!(e.target.value)} />
  );

  const nameEditor = (options: ColumnEditorOptions) => (
    <InputText type="text" value={options.value} className="expanding-editor"
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
        let val = e.target.value;
        if (val.startsWith(' ')) val = val.trimStart();
        val = val.replace(/\s{2,}/g, ' ');
        options.editorCallback!(val);
      }} />
  );

  const phoneEditor = (options: ColumnEditorOptions) => (
    <InputText keyfilter="int" maxLength={10} value={options.value} className="expanding-editor"
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => options.editorCallback!(e.target.value)} />
  );

  const numberEditor = (options: ColumnEditorOptions) => (
    <InputNumber value={options.value ?? null} className="expanding-editor" style={{ width: '100%' }}
      onValueChange={({ value }) => options.editorCallback?.(value?.toString() || null)} useGrouping={false} />
  );

  const formatDate = (value: string | Date | null | undefined) => {
    if (!value) return '';
    const date = new Date(value);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleDateString("en-gb", { day: "2-digit", month: "short", year: "numeric" });
  };

  const handleSetOrderData = (rowData: OrderModel) => {
    setSelectedOrder(rowData);
    setVisible(true);
  };

  const instructionActionTemplate = (rowData: any) => (
    <Button
      icon="pi pi-comment"
      tooltip="Customer Instruction"
      className="p-button-rounded"
      style={{ border: "none", color: "#2196F3", background: "none" }}
      onClick={() => openInstructionDialog(rowData)}
    />
  );

  const openInstructionDialog = async (rowData: OtherUserModel) => {
    setSelectedCustomerUserId(rowData.userID);
    setPendingInstructionRow(rowData);
    try {
      const response = await orderServices.getCustomerInstruction(rowData.userID);
      setInstructionText(response?.instruction ?? "");
    } catch {
      setInstructionText("");
    }
    setInstructionDialog(true);
  };

  const submitInstruction = async () => {
    if (!instructionText.trim() || !selectedCustomerUserId) {
      toast.current?.show({ severity: "warn", summary: "Validation", detail: "Please enter instruction" });
      return;
    }
    try {
      const response = await orderServices.SaveCustomerInstruction(selectedCustomerUserId, instructionText);
      if (response?.isSuccess) {
        showMessage("Instruction", response.message, "success");
        setInstructionDialog(false);
      } else {
        showMessage("Instruction", response?.message ?? "Failed to save instruction", "error");
      }
    } catch {
      showMessage("Instruction", "Error saving instruction", "error");
    }
  };

  const resetInstruction = () => setInstructionText("");

  const referredByBodyTemplate = (rowData: any) => {
    const hasReferrer = !!rowData.referredByName;

    return (
      <div
        className={`flex align-items-center justify-content-between p-2 border-round transition-colors transition-duration-150 ${!hasReferrer ? 'surface-hover' : ''}`}
        style={{ cursor: hasReferrer ? 'default' : 'pointer', border: '1px dashed #d1d5db', minHeight: '38px', minWidth: '130px', background: hasReferrer ? '#f0fdf4' : '#f9fafb' }}
        onClick={() => !hasReferrer && openReferrerDialog(rowData)}
        title={hasReferrer ? "" : "Click to assign referrer"}
      >
        <span style={{ color: hasReferrer ? '#16a34a' : '#9ca3af', fontWeight: hasReferrer ? '600' : 'normal', fontSize: '0.85rem' }}>
          {rowData.referredByName || 'Assign Referrer...'}
        </span>
        {!hasReferrer && (
          <Button icon="pi pi-pencil" className="p-button-rounded p-button-text p-button-sm p-0 m-0" style={{ width: '1.5rem', height: '1.5rem', color: '#6b7280' }} />
        )}
      </div>
    );
  };

  // ──────────────────────────────────────────────────────────────────────────
  // RENDER
  // ──────────────────────────────────────────────────────────────────────────

  return (
    <>
      <Toast ref={toast} position="top-center" />
      <div className="col-12 p-0 sm:p-2" style={{ maxWidth: '100vw', overflowX: 'hidden' }}>
        <div className="card border-noround p-1 sm:p-2" style={{ width: '100%', overflowX: 'hidden' }}>
          <DataTable value={userData.length > 0 ? userData : props.data} editMode="row" dataKey="id" onRowEditComplete={onRowEditComplete} paginator
            rows={10} rowsPerPageOptions={[5, 10, 25, 50, 100]} paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
            currentPageReportTemplate="{first} to {last} of {totalRecords}"
            responsiveLayout="stack" breakpoint="960px" className="premium-datatable" stripedRows>
            <Column field="name" header="User Name" editor={(options) => nameEditor(options)} style={{ width: '180px' }} bodyClassName="name-column" />
            <Column field="primaryContactNbr" header="Primary Contact" style={{ width: '15%' }} />
            <Column field="secondaryContactNbr" header="Secondary Contact" editor={(options) => phoneEditor(options)} style={{ width: '15%' }} />
            <Column field="email" header="Email Id" editor={(options) => textEditor(options)} style={{ width: '18%' }} />
            <Column field="referredBy" header="Referred By" body={referredByBodyTemplate} style={{ width: '15%' }} />
            <Column field="gsT_Number" header="GST No." editor={textEditor} style={{ width: '15%' }} />
            <Column field="createdDate" dataType="date" header="Created Date" style={{ width: '15%' }} body={dateBodyTemplate} />
            <Column
              field="invoiceAmount"
              header="Invoice Amount"
              style={{ width: '15%' }}
              body={(rowData: any) => (
                <span
                  style={{ color: rowData.orderId === 0 ? 'gray' : 'darkcyan', fontWeight: rowData.orderId === 0 ? 'normal' : 'bolder' }}
                  onClick={() => rowData.orderId !== 0 && handleSetOrderData(rowData)}
                >
                  {rowData.invoiceAmount === 0 ? '' : Helpers.formatAmountInINR(rowData.invoiceAmount)}
                </span>
              )}
            />
            <Column
              field="orderId"
              header="Last Order Id"
              style={{ width: '15%' }}
              body={(rowData: any) => (
                <span
                  style={{ color: rowData.orderId === 0 ? 'gray' : 'darkcyan', fontWeight: rowData.orderId === 0 ? 'normal' : 'bolder' }}
                  onClick={() => rowData.orderId !== 0 && handleSetOrderData(rowData)}
                >
                  {rowData.orderId === 0 ? '' : rowData.orderId}
                </span>
              )}
            />
            <Column field="invoiceDate" dataType="date" header="Inovice Date" style={{ width: '15%' }} body={InvoicedateBodyTemplate} />
            <Column field="orderDate" header="Last Order Date" style={{ width: '15%' }} body={OrderdateBodyTemplate} />
            <Column field="isDeleted" header="Status" body={statusBodyTemplate} style={{ width: '12%' }} />

            <Column
              body={(rowData) => instructionActionTemplate(rowData)}
              header="Instruction"
              headerStyle={{ width: "3em", textAlign: "center" }}
              bodyStyle={{ textAlign: "center" }}
            />
            <Column
              body={flagBodyTemplate}
              header="Flag"
              headerStyle={{ width: "3em", textAlign: "center" }}
              bodyStyle={{ textAlign: "center" }}
            />
            <Column
              body={flagHistoryTemplate}
              header="Flag History"
              headerStyle={{ width: "3em", textAlign: "center" }}
              bodyStyle={{ textAlign: "center" }}
            />
            <Column
              field="isEmployee"
              header="Reward Earning Restriction"
              body={employeeBodyTemplate}
              editor={(options) => employeeEditor(options)}
              style={{ width: "10%" }}
            />
            <Column
              field="isDirectEarning"
              header="Direct Reward Earning"
              body={directRewardBodyTemplate}
              editor={(options) => directRewardEditor(options)}
              style={{ width: "10%" }}
            />
            <Column rowEditor header={t('assignRouteCode_action')} headerStyle={{ minWidth: '3em', textAlign: 'right' }} bodyStyle={{ textAlign: 'right' }} />
            <Column body={(options) => deleteActionTemplate(options)} headerStyle={{ width: '2em', textAlign: 'right' }} bodyStyle={{ textAlign: 'left' }} />
          </DataTable>
        </div>
        <UserDeleteDialog
          messageHeader={notifyHeader}
          notifyMessage={notifyMessage}
          isVisible={isNotifyVisible}
          isHide={hideNotifyDialog}
        />
      </div>

      {/* ── Flag Comment Dialog ─────────────────────────────────────────────── */}
      <Dialog
        header="Reason for Flag Change"
        visible={flagCommentDialog}
        style={{ width: '35vw' }}
        breakpoints={{ '960px': '75vw', '641px': '95vw' }}
        modal
        onHide={() => setFlagCommentDialog(false)}
        footer={
          <div className="flex flex-wrap justify-content-end gap-2">
            <Button
              label="Cancel"
              className="p-button-secondary p-button-text"
              onClick={() => setFlagCommentDialog(false)}
              disabled={flagSaving}
              style={{ minWidth: '80px' }}
            />
            <Button
              label="Save"
              icon={flagSaving ? 'pi pi-spin pi-spinner' : 'pi pi-check'}
              onClick={submitFlagChange}
              disabled={flagSaving}
              style={{ backgroundColor: '#22c55e', border: 'none', minWidth: '80px' }}
            />
          </div>
        }
      >
        <div className="mb-3" style={{ fontSize: '0.9rem', color: '#64748b' }}>
          Assign reason for flag change for <strong style={{ color: '#1e293b' }}>{pendingFlagRow?.name}</strong>
        </div>
        <div className="mb-3 flex flex-wrap align-items-center gap-2" style={{ fontSize: '0.85rem' }}>
          <span style={{ color: '#64748b' }}>Changing flag to:</span>
          <div className="flex align-items-center gap-2">
            {pendingFlagValue === 0 && <span style={{ background: '#f1f5f9', color: '#64748b', borderRadius: '12px', padding: '2px 12px', fontSize: '0.75rem', fontWeight: 700 }}>No Flag</span>}
            {pendingFlagValue === 1 && <span style={{ background: '#fef2f2', color: '#ef4444', border: '1px solid #fee2e2', borderRadius: '12px', padding: '2px 12px', fontSize: '0.75rem', fontWeight: 700 }}>🔴 Red Flag</span>}
            {pendingFlagValue === 2 && <span style={{ background: '#eff6ff', color: '#3b82f6', border: '1px solid #dbeafe', borderRadius: '12px', padding: '2px 12px', fontSize: '0.75rem', fontWeight: 700 }}>🔵 Blue Flag</span>}
          </div>
        </div>
        <div className="p-field">
          <label style={{ fontWeight: 600, color: '#475569', marginBottom: '8px', display: 'block', fontSize: '0.9rem' }}>
            Comment <span style={{ color: '#94a3b8', fontSize: '0.75rem', fontWeight: 400 }}>(optional)</span>
          </label>
          <InputTextarea
            rows={4}
            autoResize
            placeholder="Enter the reason for this flag change..."
            value={flagComment}
            onChange={(e) => setFlagComment(e.target.value)}
            className="w-full"
            style={{ border: '2px solid #9bd39dff', borderRadius: '8px', padding: '0.75rem', fontSize: '0.9rem' }}
          />
        </div>
      </Dialog>

      {/* ── Flag History Dialog ─────────────────────────────────────────────── */}
      <Dialog
        header={
          <div className="flex flex-column">
            <span style={{ fontWeight: 700, fontSize: '1.25rem', color: '#1e293b' }}>Flag History</span>
            <span style={{ fontSize: '0.9rem', color: '#64748b', fontWeight: 400, marginTop: '4px' }}>
              Viewing change history for <strong style={{ color: '#1e293b' }}>{flagHistoryUser}</strong>
            </span>
          </div>
        }
        visible={flagHistoryDialog}
        style={{ width: '50vw', height: '75vh' }}
        breakpoints={{ '960px': '85vw', '641px': '95vw' }}
        contentStyle={{ display: 'flex', flexDirection: 'column', padding: 0, height: '100%' }}
        modal
        onHide={() => setFlagHistoryDialog(false)}
      >
        {flagHistoryLoading ? (
          <div className="flex justify-content-center align-items-center" style={{ padding: '2rem' }}>
            <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem', color: '#22c55e' }} />
          </div>
        ) : flagHistory.length === 0 ? (
          <p style={{ textAlign: 'center', color: '#6b7280', padding: '2rem 0' }}>No flag changes recorded for this customer.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', height: '100%', gap: '0', padding: '1rem', paddingBottom: 0 }}>
            {/* Header */}
            <div style={{ marginBottom: '0.75rem', paddingBottom: '0.5rem', borderBottom: '1px solid #e5e7eb', flexShrink: 0 }}>
              <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                <i className="pi pi-history" style={{ marginRight: '5px', color: '#22c55e' }} />
                Flag Change History
              </span>
            </div>

            {/* Timeline with SimpleBar scroll */}
            <SimpleBar style={{ flex: 1, minHeight: 0, paddingRight: '0.5rem', marginRight: '0' }} autoHide={false}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', paddingRight: '8px', paddingLeft: '1rem', paddingTop: '0.5rem' }}>
                {(() => {
                  const updateRecords = [...flagHistory]
                    .sort((a: FlagHistoryItem, b: FlagHistoryItem) => {
                      const da = a.updateDate ? new Date(a.updateDate).getTime() : 0;
                      const db = b.updateDate ? new Date(b.updateDate).getTime() : 0;
                      return db - da;
                    })
                    .filter((row: FlagHistoryItem) => row.actionType !== 'I');

                  if (updateRecords.length === 0) {
                    return (
                      <div style={{ textAlign: 'center', padding: '1.5rem 0.5rem', color: '#9ca3af', fontSize: '0.82rem', fontStyle: 'italic' }}>
                        No flag updates yet.
                      </div>
                    );
                  }

                  return updateRecords
                    .map((row: FlagHistoryItem, idx: number, arr: FlagHistoryItem[]) => {
                      const isLatest = idx === 0;
                      const entryNumber = arr.length - idx;
                      const flagColor =
                        row.flagCode === 0 ? { bg: '#f3f4f6', border: '#d1d5db', text: '#6b7280', label: '⚪ Neutral' } :
                          row.flagCode === 1 ? { bg: '#fecdd3', border: '#f87171', text: '#b91c1c', label: '🔴 Red Flag' } :
                            row.flagCode === 2 ? { bg: '#dbeafe', border: '#38bdf8', text: '#0369a1', label: '🔵 Blue Flag' } :
                              { bg: '#f3f4f6', border: '#d1d5db', text: '#6b7280', label: '⚪ Flag' };

                      return (
                        <div key={row.auditID ?? idx} style={{
                          background: isLatest ? '#f0fdf4' : '#fafafa',
                          border: `1px solid ${isLatest ? '#bbf7d0' : '#e5e7eb'}`,
                          borderRadius: '8px',
                          padding: '0.85rem',
                          position: 'relative',
                        }}>
                          {/* Entry number badge - top left */}
                          <div style={{
                            position: 'absolute', top: 8, left: 8,
                            fontSize: '0.7rem', fontWeight: 700, color: '#6b7280',
                            background: '#f3f4f6', borderRadius: '4px', padding: '2px 6px',
                          }}>#{entryNumber}</div>

                          {/* Top row: flag pill + action badge */}
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px', marginLeft: '48px' }}>
                            <span style={{
                              background: flagColor.bg, color: flagColor.text,
                              border: `1px solid ${flagColor.border}`,
                              borderRadius: '10px', padding: '2px 10px', fontSize: '0.75rem', fontWeight: 600,
                            }}>
                              {flagColor.label}
                            </span>
                            <span style={{
                              fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase',
                              color: '#b45309',
                              background: '#fef3c7',
                              borderRadius: '6px', padding: '2px 6px',
                            }}>
                              ✎ Updated
                            </span>
                          </div>

                          {/* Updated By Name + Date */}
                          <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#111827', marginLeft: '48px' }}>
                            {row.updatedByName ?? '—'}
                          </div>
                          {row.updateDate && (
                            <div style={{ fontSize: '0.7rem', color: '#6b7280', marginTop: '2px', marginLeft: '48px' }}>
                              {new Date(row.updateDate).toLocaleDateString('en-gb', { day: '2-digit', month: 'short', year: 'numeric' })}
                              {' '}
                              {new Date(row.updateDate).toLocaleTimeString('en-gb', { hour: '2-digit', minute: '2-digit' })}
                            </div>
                          )}

                          {/* Comments */}
                          {row.comments && (
                            <div style={{
                              fontSize: '0.8rem', color: '#1f2937', marginTop: '6px', marginLeft: '48px', marginRight: '8px',
                              padding: '8px 10px', background: '#ffffff', border: '1px solid #d1d5db', borderRadius: '6px',
                              fontFamily: '"Segoe UI", -apple-system, system-ui, sans-serif', lineHeight: '1.5',
                              letterSpacing: '0.3px'
                            }}>
                              "{row.comments}"
                            </div>
                          )}
                          {!row.comments && (
                            <div style={{
                              fontSize: '0.75rem', color: '#9ca3af', marginTop: '6px', marginLeft: '48px',
                              fontStyle: 'italic', fontFamily: '"Segoe UI", -apple-system, system-ui, sans-serif'
                            }}>
                              No comment provided
                            </div>
                          )}

                          {/* Latest badge */}
                          {isLatest && (
                            <span style={{
                              position: 'absolute', top: 6, right: 8,
                              fontSize: '0.58rem', background: '#22c55e', color: 'white',
                              borderRadius: '4px', padding: '2px 6px', fontWeight: 700,
                            }}>LATEST</span>
                          )}
                        </div>
                      );
                    });
                })()}
              </div>
            </SimpleBar>

            {/* Created By Footer - only show if CreatedByName exists in ANY record */}
            {flagHistory?.length > 0 && (() => {
              const createdByRecord = flagHistory.find(record => record.createdByName);
              return createdByRecord ? (
                <div style={{
                  borderTop: '1px solid #e5e7eb',
                  marginTop: '0.5rem',
                  paddingTop: '0.75rem',
                  flexShrink: 0,
                  background: '#f9fafb',
                  padding: '0.75rem 1rem',
                  borderRadius: '0',
                  marginLeft: '-1rem',
                  marginRight: '-1rem',
                  marginBottom: '-1rem',
                }}>
                  <div style={{
                    fontSize: '0.75rem', color: '#6b7280', display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 600
                  }}>
                    <i className="pi pi-user" style={{ fontSize: '0.7rem', color: '#22c55e' }} />
                    <span>Created by: <strong>{createdByRecord.createdByName}</strong></span>
                    <span style={{
                      fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase',
                      color: '#16a34a',
                      background: '#dcfce7',
                      borderRadius: '6px', padding: '2px 6px', marginLeft: '8px',
                    }}>
                      ✦ Created
                    </span>
                  </div>
                  {createdByRecord?.createdDate && (
                    <div style={{ fontSize: '0.7rem', color: '#9ca3af', marginTop: '3px', marginLeft: '22px' }}>
                      {new Date(createdByRecord.createdDate).toLocaleDateString('en-gb', { day: '2-digit', month: 'short', year: 'numeric' })}
                      {' '}
                      {new Date(createdByRecord.createdDate).toLocaleTimeString('en-gb', { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  )}
                </div>
              ) : null;
            })()}
          </div>
        )}

      </Dialog>

      {/* ── Customer Instruction Dialog ─────────────────────────────────────── */}
      <Dialog
        header="Customer Instruction"
        visible={instructionDialog}
        style={{ width: "35vw" }}
        breakpoints={{ '960px': '75vw', '641px': '95vw' }}
        modal
        onHide={() => setInstructionDialog(false)}
        footer={
          <div className="flex flex-wrap justify-content-end gap-2">
            <Button
              label="Reset"
              className="p-button-secondary p-button-text"
              onClick={resetInstruction}
              style={{ minWidth: '80px' }}
            />
            <Button
              label="Save"
              icon="pi pi-check"
              onClick={submitInstruction}
              style={{ backgroundColor: '#22c55e', border: 'none', minWidth: '100px' }}
            />
          </div>
        }
      >
        <div className="mb-4" style={{ fontSize: '0.9rem', color: '#64748b' }}>
          Add special customer instructions for <strong style={{ color: '#1e293b' }}>{pendingInstructionRow?.name}</strong>
        </div>
        <div className="p-field">
          <label style={{ fontWeight: 600, color: '#475569', marginBottom: '8px', display: 'block', fontSize: '0.9rem' }}>Enter Instruction:</label>
          <InputTextarea
            rows={4}
            autoResize
            value={instructionText}
            onChange={(e) => setInstructionText(e.target.value)}
            className="w-full"
            style={{ border: "2px solid #9bd39dff", borderRadius: "8px", padding: "0.75rem", fontSize: '0.9rem' }}
            placeholder="Type instructions here..."
          />
        </div>
      </Dialog>

      {/* ── Referred By Dialog ─────────────────────────────────────────────── */}
      <Dialog
        header="Assign Referrer"
        visible={referrerDialog}
        style={{ width: '30vw', minWidth: '400px' }}
        breakpoints={{ '960px': '75vw', '641px': '95vw' }}
        modal
        onHide={() => setReferrerDialog(false)}
        footer={
          <div className="flex justify-content-end gap-2">
            <Button
              label="Cancel"
              className="p-button-secondary p-button-text"
              onClick={() => setReferrerDialog(false)}
              disabled={referrerSaving}
              style={{ minWidth: '80px' }}
            />
            <Button
              label="Save"
              icon={referrerSaving ? 'pi pi-spin pi-spinner' : 'pi pi-check'}
              onClick={submitReferrerChange}
              disabled={referrerSaving || (pendingReferrerValue === pendingReferrerRow?.userID && pendingReferrerValue !== null)}
              style={{ backgroundColor: '#22c55e', border: 'none', minWidth: '100px' }}
            />
          </div>
        }
      >
        <div className="mb-3" style={{ fontSize: '0.9rem', color: '#64748b' }}>
          Assign tracking referrer for <strong style={{ color: '#1e293b' }}>{pendingReferrerRow?.name}</strong>
        </div>
        <div className="p-field mt-4">
          <label style={{ fontWeight: 500, marginBottom: '6px', display: 'block' }}>Search User/Mobile</label>
          <Dropdown
            id="SearchUserDialog"
            name="SearchUserDialog"
            options={orderUser}
            optionLabel="detail"
            optionValue="userID"
            value={pendingReferrerValue}
            onChange={(e: any) => setPendingReferrerValue(e.value)}
            onFilter={handleDropdownFilter} filter
            filterBy="detail"
            filterMatchMode="contains"
            placeholder="Search for User/Mobile"
            className={`w-full ${pendingReferrerValue === pendingReferrerRow?.userID && pendingReferrerValue !== null ? 'p-invalid' : ''}`}
            style={{ border: '2px solid #9bd39dff', borderRadius: '8px', minHeight: '44px' }}
            appendTo={document.body}
            resetFilterOnHide={true}
            showClear
          />
          {pendingReferrerValue === pendingReferrerRow?.userID && pendingReferrerValue !== null && (
            <small className="p-error block mt-1 font-semibold">
              <i className="pi pi-exclamation-circle mr-1" style={{ fontSize: '0.8rem' }}></i>
              A user cannot be assigned as their own referrer.
            </small>
          )}
        </div>
      </Dialog>
    </>
  );
};

export default UsersDataTable;